import { type Options, Frequency, RRule, rrulestr } from 'rrule';

export const RRULE_FREQUENCY = {
  secondly: Frequency.SECONDLY,
  minutely: Frequency.MINUTELY,
  hourly: Frequency.HOURLY,
  daily: Frequency.DAILY,
  weekly: Frequency.WEEKLY,
  monthly: Frequency.MONTHLY,
  yearly: Frequency.YEARLY,
} as const;

const BY_FIELD_RANGES: Record<
  keyof Pick<
    Options,
    | 'bysetpos'
    | 'bymonth'
    | 'bymonthday'
    | 'bynmonthday'
    | 'byyearday'
    | 'byweekno'
    | 'byweekday'
    | 'byhour'
    | 'byminute'
    | 'bysecond'
  >,
  { min: number; max: number; nonZero?: boolean }
> = {
  bysetpos: { min: -366, max: 366, nonZero: true },
  bymonth: { min: 1, max: 12 },
  bymonthday: { min: -31, max: 31, nonZero: true },
  bynmonthday: { min: -31, max: -1 },
  byyearday: { min: -366, max: 366, nonZero: true },
  byweekno: { min: -53, max: 53, nonZero: true },
  byweekday: { min: 0, max: 6 },
  byhour: { min: 0, max: 23 },
  byminute: { min: 0, max: 59 },
  bysecond: { min: 0, max: 59 },
};

export type RRuleFrequency = Frequency;
export type RRuleOptions = Options;

const MAX_TIMESTAMP = 8640000000000000;
const OCCURRENCES_LIMIT = 5;

const ruleToText = (rule: RRule) => {
  try {
    return rule.toText();
  } catch {
    return '';
  }
};

const isValidTimeZone = (tz: string) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

// the `rrule` library has some flows we need to guard against
const rruleSafeGuard = (rule: RRule) => {
  // throws on non IANA timezones when getting occurrences
  if (rule.options.tzid && !isValidTimeZone(rule.options.tzid)) {
    return false;
  }
  // hangs on invalid intervals when getting occurrences.
  if (
    typeof rule.options.interval !== 'number' ||
    isNaN(rule.options.interval) ||
    rule.options.interval <= 0
  ) {
    return false;
  }
  // hangs or throws on invalid by* fields when getting occurrences.
  for (const [field, { min, max, nonZero }] of Object.entries(
    BY_FIELD_RANGES
  ) as [
    keyof typeof BY_FIELD_RANGES,
    { min: number; max: number; nonZero?: boolean },
  ][]) {
    const value = rule.options[field];
    if (value == null) continue;
    if (
      !Array.isArray(value) ||
      value.some(
        (v) =>
          typeof v !== 'number' ||
          isNaN(v) ||
          v < min ||
          v > max ||
          (nonZero && v === 0)
      )
    ) {
      return false;
    }
  }
  return true;
};

const getOccurrences = (rule: RRule) => {
  try {
    if (!rruleSafeGuard(rule)) {
      return [];
    }
    return rule.between(
      new Date(),
      new Date(MAX_TIMESTAMP),
      false,
      (_, i) => i < OCCURRENCES_LIMIT
    );
  } catch {
    return [];
  }
};

export const optionsToRRuleString = (rruleOption: Partial<RRuleOptions>) => {
  try {
    const rrule = new RRule(rruleOption);
    return rrule.toString();
  } catch {
    return '';
  }
};

export const stringToRRule = (str: string) => {
  try {
    const rule = rrulestr(str);

    // trying to normalize timezone to IANA format for getOccurrences to
    // not throw on unsupported timezones. E.g. "America-New_York" -> "America/New_York".
    if (rule && rule.options.tzid && !isValidTimeZone(rule.options.tzid)) {
      rule.options.tzid = rule.options.tzid.replace(/-/g, '/');
    }

    return rule;
  } catch {
    return null;
  }
};

export const getRRuleDescription = (str: string) => {
  const rule = stringToRRule(str);
  if (!rule) {
    return '';
  }
  return ruleToText(rule);
};

export const getRRuleFromString = (str: string) => {
  const rule = stringToRRule(str);

  if (!rule)
    return {
      rule: null,
      occurrences: [] as Date[],
    };

  return {
    rule,
    occurrences: getOccurrences(rule),
  };
};

export const isRRuleBasic = (rruleString: string) => {
  if (!rruleString || !stringToRRule(rruleString)) {
    return true;
  }

  const isBasic =
    /^(DTSTART:\d{8}T\d{6}Z\n)?RRULE:FREQ=[^;]+;?(INTERVAL=\d+)?$/.test(
      rruleString
    ) && !/FREQ=YEARLY/.test(rruleString);
  return isBasic;
};
